import React, { useState, useEffect } from "react";
import { 
  UserPlus, 
  User, 
  CreditCard, 
  Building2, 
  Hash, 
  Save, 
  X,
  ShieldCheck,
  Search
} from "lucide-react";
import { toast } from "react-hot-toast";
import { updatePayrollDetailsApi, getEmployeesApi } from "../../../app/api";

const CreatePayrollInfoModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    account_name: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
    pan_number: ""
  });

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    try {
      setFetchingEmployees(true);
      const res = await getEmployeesApi();
      if (res.success) {
        setEmployees(res.data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setFetchingEmployees(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id) {
      toast.error("Please select an employee");
      return;
    }

    try {
      setLoading(true);
      const res = await updatePayrollDetailsApi(formData);
      if (res.success) {
        toast.success("Payroll details updated successfully!");
        onClose();
        if (onSuccess) onSuccess();
        // Reset form
        setFormData({
          user_id: "",
          account_name: "",
          account_number: "",
          bank_name: "",
          ifsc_code: "",
          pan_number: ""
        });
      } else {
        toast.error(res.message || "Failed to update payroll details");
      }
    } catch (error) {
      console.error("Error updating payroll details:", error);
      toast.error(error.message || "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto no-scrollbar rounded-[32px] shadow-2xl p-8 space-y-6 scrollbar-hide">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 text-white rounded-full transition border border-white/10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
            <UserPlus className="text-purple-400" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight text-center">Payroll Details Setup</h2>
          <p className="text-white/40 text-sm text-center">Configure banking and tax information for employees</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          
          {/* Employee Selection */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-bold text-white/30 ml-1">Select Employee</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-purple-400 transition-colors">
                <User size={18} />
              </div>
              <select
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                required
                className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-3.5 text-sm focus:outline-none focus:border-purple-500/50 focus:bg-white/[0.08] transition-all text-white appearance-none cursor-pointer"
              >
                <option value="" className="bg-[#1B103F]">Choose an employee...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id} className="bg-[#1B103F]">
                    {emp.first_name} {emp.last_name} ({emp.id})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputGroup 
              label="Bank Name" 
              name="bank_name" 
              value={formData.bank_name} 
              onChange={handleChange} 
              icon={Building2} 
              placeholder="e.g. HDFC Bank" 
              required
            />
            <InputGroup 
              label="Account Holder Name" 
              name="account_name" 
              value={formData.account_name} 
              onChange={handleChange} 
              icon={User} 
              placeholder="e.g. John Doe" 
              required
            />
            <InputGroup 
              label="Account Number" 
              name="account_number" 
              value={formData.account_number} 
              onChange={handleChange} 
              icon={CreditCard} 
              placeholder="e.g. 501004325678" 
              required
            />
            <InputGroup 
              label="IFSC Code" 
              name="ifsc_code" 
              value={formData.ifsc_code} 
              onChange={handleChange} 
              icon={Hash} 
              placeholder="e.g. HDFC0001234" 
              required
            />
            <InputGroup 
              label="PAN Number" 
              name="pan_number" 
              value={formData.pan_number} 
              onChange={handleChange} 
              icon={ShieldCheck} 
              placeholder="e.g. ABCDE1234F" 
              required
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-6">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition font-bold border border-white/10 text-white/70"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading || fetchingEmployees}
              className="flex-[2] py-4 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black shadow-xl shadow-purple-500/20 transition transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={20} /> Save Details
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputGroup = ({ label, name, type = "text", value, onChange, icon: Icon, placeholder, required = false }) => (
  <div className="space-y-2">
    <label className="text-[10px] uppercase font-bold text-white/30 ml-1">{label}</label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-purple-400 transition-colors">
        <Icon size={18} />
      </div>
      <input 
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-3.5 text-sm focus:outline-none focus:border-purple-500/50 focus:bg-white/[0.08] transition-all text-white"
      />
    </div>
  </div>
);

export default CreatePayrollInfoModal;
